import React, { useState, useEffect } from 'react';
import { Menu, Search, Clock, Calendar, TrendingUp, User, ChevronRight } from 'lucide-react';
import Spinner from './Spinner';

const OvertimeBalanceView = ({ setIsSidebarOpen }) => {
    const SERVER_BASE = process.env.REACT_APP_SERVER_BASE_URL;
    const [data, setData] = useState({ data: [], years: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // API 호출
    useEffect(() => {
        const fetchBalance = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${SERVER_BASE}/stats/annual-overtime-balance`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const result = await response.json();
                if (result.success) {
                    setData(result); 
                }
            } catch (error) {
                console.error("데이터 로딩 실패:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchBalance();
    }, [SERVER_BASE]);

    // 검색 필터링 (성명 또는 사번)
    const filteredEmployees = data.data.filter(user => 
        user.name.includes(searchTerm) || user.number.toString().includes(searchTerm)
    );

    return (
        <div className="bg-slate-50 min-h-screen">
            {/* 상단 헤더 섹션 */}
            <header className="mb-2 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                    <button 
                        onClick={() => setIsSidebarOpen(true)}
                        className="p-2 bg-white rounded-lg shadow-sm border border-slate-200 md:hidden text-slate-600"
                    >
                        <Menu size={24} />
                    </button>
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-indigo-600 rounded-lg text-white shadow-lg shadow-indigo-100">
                            <Clock size={20} />
                        </div>
                        <h2 className="text-xl md:text-2xl font-bold text-slate-800 tracking-tight">대체휴무 정보</h2>
                    </div>
                </div>

                <div className="relative w-full max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input 
                        type="text"
                        placeholder="검색"
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </header>

            {isLoading ? (
                <div className="flex justify-center py-20"><Spinner /></div>
            ) : filteredEmployees.length > 0 ? (
                <>
                    {/* --- [데스크톱 뷰] 3단 헤더 테이블 --- */}
                    <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse min-w-max">
                                <thead className="bg-slate-50/80">
                                    <tr>
                                        <th rowSpan="2" className="p-4 text-xs font-semibold text-slate-600 border-b">이름</th>
                                        {data.years.map(year => (
                                            <th key={year} colSpan="4" className="p-2 text-xs font-bold text-slate-700 border-b border-l text-center bg-slate-100/50">
                                                {year}년
                                            </th>
                                        ))}
                                        <th rowSpan="2" className="p-4 text-xs font-bold text-indigo-600 border-b border-l text-right bg-indigo-50/50">최종 잔여 대체휴무</th>
                                    </tr>
                                    <tr>
                                        {data.years.map(year => (
                                            <React.Fragment key={`${year}-sub`}>
                                                <th className="p-2 text-[10px] font-medium text-amber-600 border-b border-l text-center bg-amber-50/20">이월(A)</th>
                                                <th className="p-2 text-[10px] font-medium text-blue-500 border-b text-center">발생(B)</th>
                                                <th className="p-2 text-[10px] font-medium text-rose-400 border-b text-center">사용(C)</th>
                                                <th className="p-2 text-[10px] font-bold text-slate-700 border-b text-center bg-slate-100/30">잔여(A+B-C)</th>
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-sm">
                                    {filteredEmployees.map((user, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="p-4 font-bold text-slate-800 border-r border-slate-50">
                                                {user.name} <span className="text-[10px] font-normal text-slate-400">{user.team}</span>
                                            </td>
                                            {data.years.map(year => {
                                                const h = user.annual_history[year];
                                                return (
                                                    <React.Fragment key={`${year}-data`}>
                                                        <td className="p-3 text-center border-l text-amber-700 bg-amber-50/5 font-medium">{h.carry_over_hours}</td>
                                                        <td className="p-3 text-center text-blue-600">{h.gen_hours}</td>
                                                        <td className="p-3 text-center text-rose-500">{h.used_hours}</td>
                                                        <td className={`p-3 text-center font-bold bg-slate-50/50 ${Number(h.balance_hours) < 0 ? 'text-rose-600' : 'text-slate-800'}`}>
                                                            {h.balance_hours}
                                                        </td>
                                                    </React.Fragment>
                                                );
                                            })}
                                            <td className="p-4 text-right bg-indigo-50/30 font-black text-indigo-800 text-lg border-l border-indigo-100">
                                                {user.final_balance_hours}h
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* --- [모바일 뷰] 카드 리스트 --- */}
                    <div className="md:hidden space-y-6">
                        {filteredEmployees.map((user, idx) => (
                            <div key={idx} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                {/* 1. 상단 사용자 정보 헤더 */}
                                <div className="p-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                    <div>
                                        <div className="text-lg font-bold text-slate-800">{user.name}</div>
                                        <div className="text-xs text-slate-500 font-medium">{user.team}</div>
                                    </div>
                                </div>

                                {/* 2. 연도별 상세 데이터 섹션 */}
                                <div className="p-4 space-y-4">
                                    {data.years.map(year => {
                                        const h = user.annual_history[year];
                                        return (
                                            <div key={year} className="relative p-4 rounded-xl border border-slate-100 bg-white shadow-sm">
                                                <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 rounded-l-xl"></div>
                                                <div className="flex items-center gap-1.5 mb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">
                                                    <Calendar size={14} /> {year}년
                                                </div>
                                                
                                                <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                                                    {/* 이월 데이터 */}
                                                    <div className="bg-amber-50/40 p-2 rounded-lg border border-amber-100/50">
                                                        <div className="text-[10px] text-amber-600 font-semibold mb-0.5">이월(A)</div>
                                                        <div className="text-sm font-bold text-amber-800">{h.carry_over_hours}h</div>
                                                    </div>
                                                    
                                                    {/* 발생 데이터 */}
                                                    <div className="bg-blue-50/40 p-2 rounded-lg border border-blue-100/50">
                                                        <div className="text-[10px] text-blue-600 font-semibold mb-0.5">발생(B)</div>
                                                        <div className="text-sm font-bold text-blue-800">{h.gen_hours}h</div>
                                                    </div>

                                                    {/* 사용 데이터 */}
                                                    <div className="bg-rose-50/40 p-2 rounded-lg border border-rose-100/50">
                                                        <div className="text-[10px] text-rose-600 font-semibold mb-0.5">사용(C)</div>
                                                        <div className="text-sm font-bold text-rose-700">{h.used_hours}h</div>
                                                    </div>

                                                    {/* 연말 잔여 데이터 */}
                                                    <div className="bg-slate-800 p-2 rounded-lg shadow-inner">
                                                        <div className="text-[10px] text-slate-400 font-semibold mb-0.5">잔여(A+B-C)</div>
                                                        <div className={`text-sm font-black ${Number(h.balance_hours) < 0 ? 'text-rose-400' : 'text-white'}`}>
                                                            {h.balance_hours}h
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* 3. 최하단 최종 합계 강조 */}
                                <div className="m-4 mt-0 p-4 bg-gradient-to-br from-indigo-600 to-blue-700 rounded-xl text-white shadow-lg relative overflow-hidden">
                                    <div className="absolute right-0 bottom-0 opacity-10 translate-x-4 translate-y-4">
                                        <TrendingUp size={100} />
                                    </div>
                                    <div className="flex items-center justify-between relative z-10">
                                        <div className="space-y-0.5">
                                            <div className="text-sm font-bold">최종 잔여 대체휴무</div>
                                        </div>
                                        <div className="text-3xl font-black tracking-tighter">
                                            {user.final_balance_hours}<span className="text-xs font-normal ml-1 opacity-80">h</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-200">
                    <p className="text-slate-400 text-sm">해당하는 임직원이 없거나 데이터가 없습니다.</p>
                </div>
            )}
        </div>
    );
};

export default OvertimeBalanceView;